import { appendScript, hasScript } from "@derstandard/utils";
import { hasWebAnalyticsConsent } from "@derstandard/sourcepoint";
import type DstTagManager from "../types/gtm.d.ts";

export class TagManager {
	#gtmScript: string;
	#propertyId: number;

	constructor(config: DstTagManager.Configuration) {
		if (!config.trackingId?.length) {
			throw Error("A Google Tag Manager tracking ID is required");
		}

		this.#propertyId = config.propertyId;

		const scriptUrl = config.scriptUrl?.length
			? config.scriptUrl
			: "https://www.googletagmanager.com/gtm.js";

		this.#gtmScript = new URL(
			`${scriptUrl}?id=${config.trackingId}&l=dataLayer`,
		).href;

		this.#appendScript();
	}

	/**
	 * Pushes object to dataLayer. Initializes new dataLayer Object, if it doesn't exist yet.
	 * @param {Object} obj
	 */
	public pushToDataLayer(obj: DstTagManager.DataLayerPayload): void {
		if (!obj || Object.prototype.toString.call(obj) !== "[object Object]") {
			return;
		}

		if (this.#hasConsent()) {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push(obj);
		}
	}

	/**
	 * Appends script tag for Google Tag Manager to the markup
	 */
	#appendScript() {
		if (!hasScript(this.#gtmScript) && this.#hasConsent()) {
			console.log("Appending Google Tag Manager script", this.#gtmScript);

			window.dataLayer = window.dataLayer || [];

			window["dataLayer"].push({
				"gtm.start": new Date().getTime(),
				event: "gtm.js",
			});

			appendScript(this.#gtmScript);
		}
	}

	/**
	 * Checks whether a user has consented to web analytics, when the class was
	 * instantiated with a SourcePoint property ID. It's legitimate on some
	 * of our websites to omit this property ID (e.g. checkout)
	 */
	#hasConsent() {
		return (
			typeof this.#propertyId !== "number" ||
			hasWebAnalyticsConsent(this.#propertyId)
		);
	}
}
